import {
  UserPlus,
  Sparkles,
  MessageCircle,
  ThumbsUp,
  Link,
  Calendar,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Lead } from '../types'
import { apiDateMs, parseApiDate } from '@/lib/dates'

type SignalEventType =
  | 'imported'
  | 'enriched'
  | 'connection_sent'
  | 'dm_sent'
  | 'replied'
  | 'meeting_booked'

type SignalEvent = {
  readonly type: SignalEventType
  readonly label: string
  readonly date: string
  readonly detail?: string
}

function buildSignalEvents(lead: Lead): readonly SignalEvent[] {
  const events: SignalEvent[] = []

  // Import event
  events.push({
    type: 'imported',
    label: 'Lead imported',
    date: lead.created_at,
    detail: `Source: LinkedIn search`,
  })

  // Enrichment event
  if (lead.enriched_at) {
    events.push({
      type: 'enriched',
      label: 'AI enrichment completed',
      date: lead.enriched_at,
      detail: 'Company signals, pain points, and strategy recommendation generated.',
    })
  }

  // Activity events based on status progression
  if (
    lead.status === 'connection_sent' ||
    lead.status === 'connected' ||
    lead.status === 'dm_sent' ||
    lead.status === 'replied' ||
    lead.status === 'qualified' ||
    lead.status === 'meeting_booked' ||
    lead.status === 'won'
  ) {
    events.push({
      type: 'connection_sent',
      label: 'Connection request sent',
      date: lead.last_activity_at ?? lead.created_at,
    })
  }

  if (
    lead.status === 'replied' ||
    lead.status === 'qualified' ||
    lead.status === 'meeting_booked' ||
    lead.status === 'won'
  ) {
    events.push({
      type: 'replied',
      label: 'Lead replied',
      date: lead.last_activity_at ?? lead.created_at,
      detail: lead.last_message_text
        ? `"${lead.last_message_text.slice(0, 60)}${lead.last_message_text.length > 60 ? '…' : ''}"`
        : undefined,
    })
  }

  if (lead.status === 'meeting_booked' || lead.status === 'won') {
    events.push({
      type: 'meeting_booked',
      label: 'Meeting booked',
      date: lead.last_activity_at ?? lead.created_at,
    })
  }

  // Sort chronologically (oldest first). apiDateMs handles the DB's mixed
  // ISO/space timestamp formats — new Date().getTime() returned NaN for the
  // space form on Safari, and a NaN comparator scrambles the whole sort.
  return events.sort((a, b) => apiDateMs(a.date) - apiDateMs(b.date))
}

const EVENT_CONFIG: Record<
  SignalEventType,
  { icon: React.ReactNode; color: string }
> = {
  imported: {
    icon: <Link className="h-3 w-3" />,
    color: 'bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)] dark:text-[var(--color-silver)]',
  },
  enriched: {
    icon: <Sparkles className="h-3 w-3" />,
    color: 'bg-[rgba(28,63,255,0.12)] text-purple-600 dark:text-purple-400',
  },
  connection_sent: {
    icon: <UserPlus className="h-3 w-3" />,
    color: 'bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] ',
  },
  dm_sent: {
    icon: <MessageCircle className="h-3 w-3" />,
    color: 'bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] ',
  },
  replied: {
    icon: <ThumbsUp className="h-3 w-3" />,
    color: 'bg-emerald-500/12 text-emerald-300 ',
  },
  meeting_booked: {
    icon: <Calendar className="h-3 w-3" />,
    color: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400',
  },
}

function formatEventDate(dateStr: string): string {
  const date = parseApiDate(dateStr)
  if (!date) return '-'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMins = Math.floor(diffMs / (1000 * 60))

  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

type SignalTimelineProps = {
  readonly lead: Lead
}

export function SignalTimeline({ lead }: SignalTimelineProps) {
  const events = buildSignalEvents(lead)

  if (events.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-4 text-center">
        No timeline events yet.
      </p>
    )
  }

  return (
    <div className="relative space-y-0">
      {events.map((event, index) => {
        const config = EVENT_CONFIG[event.type]
        const isLast = index === events.length - 1

        return (
          <div key={`${event.type}-${event.date}`} className="flex gap-3">
            {/* Icon + vertical line */}
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                  config.color,
                )}
              >
                {config.icon}
              </div>
              {!isLast && <div className="w-px flex-1 bg-border my-1" style={{ minHeight: '16px' }} />}
            </div>

            {/* Content */}
            <div className={cn('pb-4 min-w-0', isLast && 'pb-0')}>
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-medium">{event.label}</span>
                <span className="text-[10px] text-muted-foreground shrink-0">
                  {formatEventDate(event.date)}
                </span>
              </div>
              {event.detail && (
                <p className="mt-0.5 text-[10px] text-muted-foreground leading-relaxed">
                  {event.detail}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
